import { SCOPE_REACH, type NoteLearning } from '@/shared/triage/guidelines';
import { Button } from '@/shared/ui/Button';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';

import {
  learningText,
  onlyThisEmailText,
  REPLACED_TEXT,
  TURNED_OFF_TEXT,
} from './noteLearningText';
import { useUndoLearning } from './useEmails';

/**
 * What the platform made of the note on this email (contract v10.35, ADR 0030 of the back).
 *
 * The note written in «Corregir…» is distilled by the worker into a criterion with a
 * reach, a few seconds after it is saved: until then `pendiente` (the detail asks again,
 * `noteLearningPending`), then what it learned in the shop's words, how far it reaches,
 * what the distillation noticed (`remarks`: the note and the click disagreed, the note
 * wins) and «Deshacer» for whoever may turn it off. Nothing here changes this email: a
 * criterion acts from the next one on.
 *
 * Rendered inside a live region that is always mounted, so «Aprendiendo…» turning into
 * «Aprendido: …» is announced.
 */
export function NoteLearningNote({
  emailId,
  learning,
}: {
  emailId: string;
  learning: NoteLearning;
}) {
  const undo = useUndoLearning(emailId);

  if (learning.status === 'pendiente') {
    return <p className="text-ink-3 text-[13px]">{learningText(learning)}</p>;
  }
  if (learning.status === 'solo_este_correo') {
    return <p className="text-ink-3 text-[13px]">{onlyThisEmailText(learning)}</p>;
  }
  if (learning.status === 'desactivado') {
    return <p className="text-ink-3 text-[13px]">{TURNED_OFF_TEXT}</p>;
  }
  if (learning.status === 'sustituido') {
    return <p className="text-ink-3 text-[13px]">{REPLACED_TEXT}</p>;
  }

  const reach = learning.scope ? SCOPE_REACH[learning.scope] : null;
  const guidelineId = learning.guideline_id ?? null;
  return (
    <div className="bg-paper-2 text-ink-2 flex flex-col gap-1.5 rounded-md px-3 py-2 text-[13px]">
      <p>
        <span className="text-ink font-medium">Aprendido: </span>
        {learning.text}
      </p>
      {reach ? <p className="text-ink-3">Vale {reach}, desde el siguiente correo.</p> : null}
      {learning.remarks ? <p className="text-ink-3 italic">{learning.remarks}</p> : null}
      {learning.can_undo && guidelineId ? (
        <div>
          <Button
            variant="secondary"
            size="sm"
            disabled={undo.isPending}
            onClick={() => undo.mutate(guidelineId)}
          >
            {undo.isPending ? 'Deshaciendo…' : 'Deshacer'}
          </Button>
        </div>
      ) : null}
      {undo.isError ? <ErrorMessage error={undo.error}>No se pudo deshacer</ErrorMessage> : null}
    </div>
  );
}
